'use client';
import { useCallback, useEffect, useState } from 'react';
import Script from 'next/script';
import { usePathname } from 'next/navigation';
import { GOOGLE_CLIENT_ID } from '@/_config/env';
import { googleLogin } from '@/app/login/_helpers';

// Pages that already show an explicit "Continue with Google" button —
// skipped here so only one `google.accounts.id.initialize()` call (and
// callback) is ever active on a given page at a time.
const EXCLUDED_PATHS = ['/login', '/cart', '/checkout'];

const parseCookie = (name) => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Google One Tap — the small auto-prompt offering guests a quick sign-in
 * without clicking anything. Mounted globally; no-ops for logged-in users
 * and on pages that already have their own Google button.
 */
export const GoogleOneTap = () => {
    const pathname = usePathname();
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const isExcludedPath = EXCLUDED_PATHS.some((path) => pathname?.startsWith(path));

    const handleCredentialResponse = useCallback(async (response) => {
        if (!response?.credential) return;
        const result = await googleLogin(response.credential);
        const token        = result?.data?.token;
        const user         = result?.data?.user;
        const refreshToken = result?.data?.refreshToken;
        const expiresIn    = result?.data?.expiresIn;
        if (result?.success && token) {
            await fetch('/api/auth/set-session', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ token, refreshToken, expiresIn, user }),
            });
            // No page reload — tell the header to re-read the auth cookie
            // now so it reflects the new session instantly.
            window.dispatchEvent(new Event('ennix:auth-changed'));
        }
    }, []);

    // Script may already be loaded from a previous mount (client-side nav).
    useEffect(() => {
        if (window.google?.accounts?.id) setScriptLoaded(true);
    }, []);

    useEffect(() => {
        if (isExcludedPath || !scriptLoaded || !window.google?.accounts?.id) return;
        if (parseCookie('authUser')) return; // already logged in

        window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleCredentialResponse,
            auto_select: false,
            cancel_on_tap_outside: true,
            // Chrome's third-party-cookie phase-out silently blocks the legacy
            // One Tap prompt unless FedCM is opted into — without this it can
            // simply never render, with no visible error.
            use_fedcm_for_prompt: true,
        });
        window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed()) {
                console.warn('[GoogleOneTap] not displayed:', notification.getNotDisplayedReason());
            } else if (notification.isSkippedMoment()) {
                console.warn('[GoogleOneTap] skipped:', notification.getSkippedReason());
            }
        });

        return () => {
            window.google?.accounts?.id?.cancel();
        };
    }, [scriptLoaded, isExcludedPath, handleCredentialResponse]);

    if (isExcludedPath) return null;

    return (
        <Script
            id="google-identity-client"
            src="https://accounts.google.com/gsi/client"
            strategy="afterInteractive"
            onReady={() => setScriptLoaded(true)}
        />
    );
};
